import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import { AccountId } from "../ids/definitions";
import { MongoTeamId, MongoUserId } from "../ids/mongo-ids";

/**
 * Assignee picker payload — users on the team that owns the relevant ticket
 * scope. Listing the wrong team's users would let the merchant pick an
 * assignee the server immediately rejects (the support-v2 update path
 * validates assignee membership against the ticket's team).
 */
export const AssignableUser = Schema.Struct({
  _id: MongoUserId,
  /** CRDB account the user belongs to. Lets connected-team callers slice
   * the global user list per team without an extra round-trip. */
  accountId: AccountId,
  name: Schema.String,
  email: Schema.String,
});
export type AssignableUser = typeof AssignableUser.Type;

export const GetAccessibleSupportUsersResponse = Schema.Struct({
  users: Schema.Array(AssignableUser),
});
export type GetAccessibleSupportUsersResponse =
  typeof GetAccessibleSupportUsersResponse.Type;

/**
 * A team the caller can see support tickets for (their home team plus every
 * connected team their same-email user has VIEW_SUPPORT on). Powers the
 * optional "Team" column and the team filter in the support-ticket table.
 * `accountId` is the OpenSearch/list scope key; `teamId` and `name` are for
 * display and routing.
 */
export const AccessibleSupportTeam = Schema.Struct({
  accountId: AccountId,
  teamId: MongoTeamId,
  name: Schema.String,
});
export type AccessibleSupportTeam = typeof AccessibleSupportTeam.Type;

export const GetAccessibleSupportTeamsResponse = Schema.Struct({
  teams: Schema.Array(AccessibleSupportTeam),
});
export type GetAccessibleSupportTeamsResponse =
  typeof GetAccessibleSupportTeamsResponse.Type;

/**
 * Customer search hit shape. Returned by the per-team customer search
 * endpoint that backs the support-v2 "Change customer" picker. Mirrors the
 * fields the existing `CustomerSearchItem` exposes so the frontend can
 * adapt without UI changes.
 */
export const SupportCustomerSearchItem = Schema.Struct({
  id: Schema.String,
  customerName: Schema.String,
  email: Schema.NullOr(Schema.String),
  phone: Schema.NullOr(Schema.String),
});
export type SupportCustomerSearchItem = typeof SupportCustomerSearchItem.Type;

export const SearchSupportCustomersResponse = Schema.Struct({
  results: Schema.Array(SupportCustomerSearchItem),
});
export type SearchSupportCustomersResponse =
  typeof SearchSupportCustomersResponse.Type;

/**
 * Related-record endpoints are keyed on the Mongo `teamId` the lookup runs
 * against — Order, Return, and Customer documents are team-scoped in Mongo,
 * and the merchant frontend's TanStack DB collections filter local rows by
 * `order.team` / `return.team`, so using the same identifier end-to-end
 * lets one collection serve multiple connected teams without cross-team
 * leakage. The connected-team middleware validates that the caller has the
 * relevant permission on the supplied `teamId`.
 */
const teamUrlParams = Schema.Struct({
  teamId: MongoTeamId,
  email: Schema.String,
  limit: Schema.optional(Schema.NumberFromString),
});

const teamCustomerUrlParams = Schema.Struct({
  teamId: MongoTeamId,
  email: Schema.String,
});

const teamCustomerSearchUrlParams = Schema.Struct({
  teamId: MongoTeamId,
  searchText: Schema.String,
  limit: Schema.optional(Schema.NumberFromString),
});

const passthroughList = Schema.Array(Schema.Unknown);

/**
 * Order, return, and customer payloads stay as `Schema.Unknown` — the
 * merchant frontend already handles the rich Mongoose document shapes that
 * live in `redo-model`. Re-mirroring them in effect-schema would duplicate
 * hundreds of fields for no validation benefit.
 */
export const SupportTicketRelatedRecordsGroup = HttpApiGroup.make(
  "supportTicketRelatedRecords",
)
  .add(
    endpoint.merchant
      .get("getSupportRelatedOrdersByEmail", "/support-related-records/orders")
      .setUrlParams(teamUrlParams)
      .addSuccess(Schema.Struct({ orders: passthroughList }))
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getSupportRelatedReturnsByEmail",
        "/support-related-records/returns",
      )
      .setUrlParams(teamUrlParams)
      .addSuccess(Schema.Struct({ returns: passthroughList }))
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getSupportRelatedCustomerByEmail",
        "/support-related-records/customer",
      )
      .setUrlParams(teamCustomerUrlParams)
      .addSuccess(Schema.NullOr(Schema.Unknown))
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "searchSupportCustomers",
        "/support-related-records/search-customers",
      )
      .setUrlParams(teamCustomerSearchUrlParams)
      .addSuccess(SearchSupportCustomersResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getAccessibleSupportUsers",
        "/support-related-records/accessible-users",
      )
      .addSuccess(GetAccessibleSupportUsersResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getAccessibleSupportTeams",
        "/support-related-records/accessible-teams",
      )
      .addSuccess(GetAccessibleSupportTeamsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");

export { HttpApi } from "@effect/platform";
